import React, { useEffect, useState } from 'react';
import {
  fetchChecklist,
  createChecklistItem,
  updateChecklistItem,
  deleteChecklistItem,
  ChecklistItem,
} from '../../api/checklist';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Checkbox } from '../ui/checkbox';
import { cn } from '../../lib/utils';

interface ChecklistPanelProps {
  canManage: boolean;
}

export const ChecklistPanel: React.FC<ChecklistPanelProps> = ({ canManage }) => {
  const [items, setItems] = useState<ChecklistItem[]>([]);
  const [newItem, setNewItem] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const token = localStorage.getItem('access_token');
    if (!token) {
      setLoading(false);
      setError('Missing access token.');
      return;
    }

    fetchChecklist(token)
      .then(setItems)
      .catch(() => setError('Failed to load checklist'))
      .finally(() => setLoading(false));
  }, []);

  const handleAdd = async (e: React.FormEvent) => {
    e.preventDefault();
    const token = localStorage.getItem('access_token');
    if (!token || !newItem.trim()) return;
    try {
      const item = await createChecklistItem(token, newItem.trim());
      setItems(prev => [...prev, item]);
      setNewItem('');
    } catch {
      setError('Failed to add item');
    }
  };

  const handleToggle = async (id: number, completed: boolean) => {
    const token = localStorage.getItem('access_token');
    if (!token) return;
    try {
      const updated = await updateChecklistItem(token, id, completed);
      setItems(prev => prev.map(item => (item.id === id ? updated : item)));
    } catch {
      setError('Failed to update item');
    }
  };

  const handleDelete = async (id: number) => {
    const token = localStorage.getItem('access_token');
    if (!token) return;
    try {
      await deleteChecklistItem(token, id);
      setItems(prev => prev.filter(item => item.id !== id));
    } catch {
      setError('Failed to delete item');
    }
  };

  if (loading) {
    return (
      <div className="rounded-xl border border-border bg-card p-6 shadow-sm">
        <div className="flex items-center justify-between">
          <div className="h-4 w-32 animate-pulse rounded bg-muted" />
          <div className="h-8 w-20 animate-pulse rounded bg-muted" />
        </div>
        <div className="mt-6 space-y-3">
          <div className="h-12 animate-pulse rounded-lg border border-dashed border-muted" />
          <div className="h-12 animate-pulse rounded-lg border border-dashed border-muted" />
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4 rounded-xl border border-border bg-card p-6 shadow-sm">
      {error && (
        <div className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive">
          {error}
        </div>
      )}
      {canManage && (
        <form onSubmit={handleAdd} className="flex flex-col gap-3 sm:flex-row">
          <Input
            value={newItem}
            onChange={e => setNewItem(e.target.value)}
            placeholder="Add new checklist item"
            className="sm:flex-1"
          />
          <Button type="submit" className="sm:w-auto">
            Add
          </Button>
        </form>
      )}
      <ul className="space-y-3">
        {items.map(item => (
          <li
            key={item.id}
            className="flex items-center justify-between gap-3 rounded-lg border border-border bg-background px-4 py-3 shadow-sm"
          >
            <label className="flex flex-1 items-center gap-3">
              <Checkbox
                checked={item.completed}
                onCheckedChange={checked => handleToggle(item.id, Boolean(checked))}
                aria-label={`Toggle ${item.text}`}
              />
              <span
                className={cn(
                  'text-sm font-medium text-foreground transition-all',
                  item.completed && 'text-muted-foreground line-through'
                )}
              >
                {item.text}
              </span>
            </label>
            {canManage && (
              <Button type="button" variant="ghost" size="sm" onClick={() => handleDelete(item.id)}>
                Delete
              </Button>
            )}
          </li>
        ))}
        {items.length === 0 && (
          <li className="rounded-lg border border-dashed border-border/80 bg-muted/40 px-4 py-6 text-center text-sm text-muted-foreground">
            No checklist items yet. {canManage ? 'Add one to get started.' : 'Check back later.'}
          </li>
        )}
      </ul>
    </div>
  );
};
